import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BOURGOGNE } from "@/data";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.06, ease: "easeOut" } }),
};

const find = (title) => BOURGOGNE.find((b) => b.title === title);

const GROUPS = [
  { label: "Patrimoine", items: ["Le château de Tanlay", "Le château d'Ancy-le-Franc"] },
  { label: "Nature et mouvement", items: ["Le canal de Bourgogne", "Promenades à pied et à vélo"] },
  { label: "Vignobles et art de vivre", items: ["Chablis et ses vignobles"] },
];

function Card({ b, i }) {
  if (!b) return null;
  return (
    <motion.a href={b.link} target="_blank" rel="noopener noreferrer" data-testid={`bourgogne-${b.title}`}
      initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i} className="group block">
      <div className="overflow-hidden rounded-2xl mb-5">
        <img src={b.img} alt={b.alt || b.title} className="w-full h-60 object-cover group-hover:scale-105 transition-transform duration-700" />
      </div>
      <h3 className="font-serif text-2xl text-forest">{b.title}</h3>
      <p className="text-charcoal/70 mt-2 leading-relaxed">{b.text}</p>
      {b.credit && <p className="text-[11px] text-charcoal/40 mt-2">{b.credit}</p>}
      <span className="inline-flex items-center gap-2 mt-3 text-forest text-sm font-medium group-hover:gap-3 transition-all">
        Découvrir <ArrowRight className="w-4 h-4" />
      </span>
    </motion.a>
  );
}

export default function Bourgogne() {
  return (
    <div data-testid="page-bourgogne" className="py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-16">
          <span className="text-xs font-semibold uppercase tracking-[0.28em] text-terracotta">Découvrir</span>
          <h1 className="font-serif text-5xl lg:text-6xl font-light mt-5 text-forest">La Bourgogne, sans programme imposé.</h1>
          <p className="text-charcoal/70 mt-6 text-lg">
            Depuis Tanlay, partez à la découverte d'une Bourgogne confidentielle, entre châteaux Renaissance, canal, vignobles et chemins de campagne. Composez librement votre séjour au gré de vos envies.
          </p>
        </div>

        <div className="space-y-16">
          {GROUPS.map((grp) => (
            <div key={grp.label} data-testid={`bourgogne-group-${grp.label}`}>
              <h2 className="text-xs font-semibold uppercase tracking-[0.28em] text-brown mb-7 pb-3 border-b border-sand">{grp.label}</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {grp.items.map((t, i) => <Card key={t} b={find(t)} i={i} />)}
              </div>
            </div>
          ))}
        </div>

        <p data-testid="bourgogne-disclaimer" className="text-xs text-charcoal/40 mt-12">
          Visuels d'ambiance. Les liens conduisent vers les sites officiels des lieux et activités présentés.
        </p>

        {/* Encart Carnet de voyage */}
        <Link to="/carnet-de-voyage-tanlay" data-testid="bourgogne-carnet" className="group block mt-16">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
            className="grid md:grid-cols-[300px_1fr] gap-8 items-center bg-stone/50 border border-sand rounded-2xl p-8 lg:p-10">
            <div className="overflow-hidden rounded-xl bg-[#eee8d8] p-3 rotate-[-1deg] shadow-md">
              <img src="/images/carnet/ancien-tanlay.jpg" alt="Carte postale ancienne de Tanlay, vue générale et port du canal" className="w-full aspect-[1.55] object-contain bg-[#e5dfd0]" />
              <p className="text-center font-serif text-sm text-charcoal/70 pt-3 pb-1">Tanlay, vue générale et port du canal</p>
            </div>
            <div>
              <span className="text-xs font-semibold uppercase tracking-[0.28em] text-terracotta">Le carnet des échappées</span>
              <h2 className="font-serif text-3xl lg:text-4xl font-light text-forest mt-4">Carnet de voyage</h2>
              <p className="text-charcoal/70 mt-4 leading-relaxed">
                Explorez Tanlay, Noyers et le Tonnerrois à travers cartes postales anciennes, récits historiques, châteaux, canal de Bourgogne et portraits de femmes.
              </p>
              <span className="inline-flex items-center gap-2 mt-5 text-forest font-medium group-hover:gap-3 transition-all">
                Entrer dans le voyage <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </motion.div>
        </Link>

        {/* Bloc final */}
        <div data-testid="bourgogne-cta" className="mt-16 bg-forest text-cream rounded-2xl px-8 py-14 lg:px-14 text-center">
          <h2 className="font-serif text-3xl lg:text-4xl font-light">Et si vous prolongiez l'expérience ?</h2>
          <p className="text-cream/75 mt-4 max-w-xl mx-auto">Choisissez votre gîte à Tanlay et imaginez votre séjour au cœur de la Bourgogne.</p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/chez-anne-tanlay">
              <Button data-testid="cta-chez-anne" className="rounded-full bg-cream hover:bg-white text-forest px-7 h-11">Découvrir Chez Anne</Button>
            </Link>
            <Link to="/au-19-tanlay">
              <Button data-testid="cta-au-19" className="rounded-full bg-cream hover:bg-white text-forest px-7 h-11">Découvrir Au 19</Button>
            </Link>
            <Link to="/contact#reserver" data-testid="cta-booking">
              <Button variant="outline" className="rounded-full border-cream/50 text-cream bg-transparent hover:bg-cream hover:text-forest px-7 h-11">
                Choisir mon gîte
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
